import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '@ohif/ui-next';
import './FormMultiSelect.css';

interface FormMultiSelectProps {
  label: string;
  required?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const FormMultiSelect: React.FC<FormMultiSelectProps> = ({
  label,
  required = false,
  options = [],
  placeholder = 'Select',
  value = [],
  onChange,
  error,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (optionValue: string) => {
    if (disabled) {
      return;
    }

    const currentValues = value || [];
    const isSelected = currentValues.includes(optionValue);

    if (isSelected) {
      onChange(currentValues.filter(v => v !== optionValue));
    } else {
      onChange([...currentValues, optionValue]);
    }
  };

  const handleRemoveChip = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    const currentValues = value || [];
    onChange(currentValues.filter(v => v !== optionValue));
  };

  const list = Array.isArray(options) ? options : [];
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedOptions = list.filter(opt => selectedValues.includes(opt.value));
  const hasChips = selectedValues.length > 0;

  return (
    <div
      ref={dropdownRef}
      className={`form-multi-select-container ${className}`.trim()}
    >
      <div className="form-multi-select-label-wrapper">
        <label className="form-multi-select-label">{label}</label>
        {required ? <span className="form-multi-select-required">*</span> : null}
      </div>

      <div className="form-multi-select-field-wrap">
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={[
            'form-multi-select-field',
            hasChips ? 'form-multi-select-field--has-chips' : '',
            isOpen ? 'form-multi-select-field--open' : '',
            disabled ? 'form-multi-select-field--disabled' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {hasChips ? (
            selectedOptions.map(option => (
              <div
                key={option.value}
                className="form-multi-select-chip"
                onClick={e => e.stopPropagation()}
              >
                <span>{option.label}</span>
                <button
                  type="button"
                  className="form-multi-select-chip-remove"
                  onClick={e => handleRemoveChip(option.value, e)}
                  aria-label={`Remove ${option.label}`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 3L3 9M3 3L9 9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ))
          ) : (
            <span className="form-multi-select-placeholder">{placeholder}</span>
          )}
          <div
            className={`form-multi-select-chevron ${isOpen ? 'form-multi-select-chevron--open' : ''}`}
          >
            <Icons.ChevronDown style={{ width: '20px', height: '20px' }} />
          </div>
        </div>

        {isOpen && !disabled ? (
          <div className="form-multi-select-dropdown">
            {options.length === 0 ? (
              <div className="form-multi-select-option">
                <span className="form-multi-select-option-label">No options found</span>
              </div>
            ) : (
              options.map(option => {
                const isSelected = value.includes(option.value);
                return (
                  <div
                    key={option.value}
                    onClick={() => handleToggle(option.value)}
                    className={`form-multi-select-option ${isSelected ? 'form-multi-select-option--selected' : ''}`}
                  >
                    <div
                      className={`form-multi-select-checkbox ${isSelected ? 'form-multi-select-checkbox--checked' : ''}`}
                    >
                      {isSelected ? (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </div>
                    <span className="form-multi-select-option-label">{option.label}</span>
                  </div>
                );
              })
            )}
          </div>
        ) : null}
      </div>

      {error ? <span className="form-multi-select-error">{error}</span> : null}
    </div>
  );
};

export default FormMultiSelect;
